/**
 * AxLabs Physics Experiments - Modern Physics
 *
 * Collection of modern physics experiments covering quantum mechanics,
 * relativity, atomic physics, and nuclear physics.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// Physical constants
const H = 6.62607015e-34; // Planck constant (J·s)
const C = 299792458; // Speed of light (m/s)
const E_CHARGE = 1.602176634e-19; // Elementary charge (C)
const M_E = 9.1093837015e-31; // Electron mass (kg)
const K_B = 1.380649e-23; // Boltzmann constant (J/K)
const RYDBERG = 1.0973731568160e7; // Rydberg constant (m⁻¹)
const STEFAN_BOLTZMANN = 5.670374419e-8; // Stefan-Boltzmann constant (W/m²/K⁴)
// ============================================================================
// PHOTOELECTRIC EFFECT
// ============================================================================
export const PhotoelectricEffect = createExperimentFromSchema({
    id: 'photoelectric-effect',
    name: 'Photoelectric Effect',
    domain: 'physics',
    description: 'Emission of electrons when light strikes a material surface',
    conceptTags: ['Photoelectric Effect', 'Photons', 'Work Function', 'Quantum Theory'],
    difficulty: 5,
    stateVariables: [
        { id: 'lambda', name: 'Wavelength', unit: 'm', initial: 400e-9, symbol: '\\lambda', description: 'Incident light wavelength' },
    ],
    parameters: [
        { id: 'intensity', name: 'Light Intensity', unit: 'W/m²', min: 1, max: 1000, default: 100, symbol: 'I' },
        { id: 'phi', name: 'Work Function', unit: 'eV', min: 1, max: 6, default: 2.3, symbol: '\\phi', description: 'Minimum energy to eject electron (eV)' },
        { id: 'material', name: 'Material Type', unit: '', min: 0, max: 4, default: 0, symbol: 'mat', description: '0=Sodium(2.3eV), 1=Zinc(4.3eV), 2=Copper(4.7eV), 3=Silver(4.7eV), 4=Platinum(6.35eV)' },
    ],
    equations: [
        {
            id: 'einstein-equation',
            name: "Einstein's Photoelectric Equation",
            latex: 'hf = \\phi + K_{max}',
            description: 'Photon energy equals work function plus max kinetic energy',
        },
        {
            id: 'photon-energy',
            name: 'Photon Energy',
            latex: 'E = hf = \\frac{hc}{\\lambda}',
            description: 'Energy of a single photon',
        },
        {
            id: 'max-kinetic',
            name: 'Maximum Kinetic Energy',
            latex: 'K_{max} = \\frac{hc}{\\lambda} - \\phi',
            description: 'Maximum kinetic energy of ejected electrons',
        },
        {
            id: 'cutoff-frequency',
            name: 'Cutoff Frequency',
            latex: 'f_0 = \\frac{\\phi}{h}',
            description: 'Minimum frequency to eject electrons',
        },
    ],
    derivatives: () => ({
        lambda: 0,
    }),
    derivedState: (state, params) => {
        const lambda = getState(state, 'lambda');
        const intensity = getParam(params, 'intensity');
        const phi_eV = getParam(params, 'phi');
        // Use the phi parameter directly as work function
        const phi = phi_eV;
        const phi_joules = phi * E_CHARGE;
        const photon_energy = (H * C) / lambda; // Joules
        const photon_energy_eV = photon_energy / E_CHARGE; // eV
        const K_max_joules = Math.max(0, photon_energy - phi_joules);
        const K_max_eV = Math.max(0, photon_energy_eV - phi);
        const K_max_joules_alt = K_max_eV * E_CHARGE;
        const v_max = K_max_eV > 0 ? Math.sqrt(2 * K_max_joules_alt / M_E) : 0;
        const lambda_0 = (H * C) / (phi * E_CHARGE); // Cutoff wavelength
        const f_0 = (phi * E_CHARGE) / H; // Cutoff frequency
        const current = K_max_eV > 0 ? intensity * 1e-6 : 0; // Proportional to intensity if above threshold
        return {
            photon_energy_eV,
            photon_energy_joules: photon_energy,
            work_function_eV: phi,
            max_kinetic_energy_eV: K_max_eV,
            max_kinetic_energy_joules: K_max_joules,
            max_electron_velocity: v_max,
            cutoff_wavelength: lambda_0,
            cutoff_frequency: f_0,
            photoelectron_emitted: photon_energy_eV > phi ? 1 : 0,
            photocurrent: current,
            stopping_voltage: K_max_eV, // Stopping voltage in volts
        };
    },
    energyComputation: (state, params) => {
        const lambda = getState(state, 'lambda');
        const phi_eV = getParam(params, 'phi');
        const photon_energy = (H * C) / lambda;
        const photon_energy_eV = photon_energy / E_CHARGE;
        const K_max_eV = Math.max(0, photon_energy_eV - phi_eV);
        return {
            kinetic: K_max_eV * E_CHARGE,
            potential: 0,
            thermal: 0,
            total: photon_energy,
        };
    },
    failureModes: [
        {
            id: 'below-threshold',
            name: 'Below Threshold Frequency',
            check: (state, params) => {
                const lambda = getState(getStateValues(state), 'lambda');
                const phi = getParam(params, 'phi');
                const photon_energy_eV = ((H * C) / lambda) / E_CHARGE;
                return photon_energy_eV < phi;
            },
            severity: 'warning',
            educationalMessage: 'The photon energy is below the work function of the material. No photoelectrons will be emitted regardless of intensity!',
        },
    ],
    bounds: {
        lambda: { min: 100e-9, max: 1000e-9 },
    },
});
// ============================================================================
// COMPTON SCATTERING
// ============================================================================
export const ComptonScattering = createExperimentFromSchema({
    id: 'compton-scattering',
    name: 'Compton Scattering',
    domain: 'physics',
    description: 'Photon scattering off electrons with wavelength shift',
    conceptTags: ['Compton Scattering', 'Photon Momentum', 'Particle-Wave Duality', 'X-Rays'],
    difficulty: 6,
    stateVariables: [
        { id: 'theta', name: 'Scattering Angle', unit: 'rad', initial: Math.PI / 2, symbol: '\\theta', description: 'Angle of scattered photon' },
    ],
    parameters: [
        { id: 'lambda_initial', name: 'Initial Wavelength', unit: 'm', min: 1e-12, max: 1e-9, default: 1e-11, symbol: '\\lambda_i', description: 'Typical X-ray wavelength' },
    ],
    equations: [
        {
            id: 'compton-shift',
            name: 'Compton Shift',
            latex: '\\Delta\\lambda = \\lambda\' - \\lambda = \\frac{h}{m_ec}(1 - \\cos\\theta)',
            description: 'Change in photon wavelength due to scattering',
        },
        {
            id: 'compton-wavelength',
            name: 'Compton Wavelength of Electron',
            latex: '\\lambda_c = \\frac{h}{m_ec} \\approx 2.43 \\times 10^{-12} \\text{ m}',
            description: 'Fundamental constant for electron scattering',
        },
        {
            id: 'energy-transfer',
            name: 'Energy Transferred to Electron',
            latex: '\\Delta E = hc\\left(\\frac{1}{\\lambda} - \\frac{1}{\\lambda\'}\\right)',
            description: 'Kinetic energy gained by recoil electron',
        },
    ],
    derivatives: () => ({
        theta: 0,
    }),
    derivedState: (state, params) => {
        const theta = getState(state, 'theta');
        const lambda_i = getParam(params, 'lambda_initial');
        const lambda_c = H / (M_E * C); // Compton wavelength of electron
        const delta_lambda = lambda_c * (1 - Math.cos(theta));
        const lambda_f = lambda_i + delta_lambda;
        const E_i = (H * C) / lambda_i; // Initial photon energy
        const E_f = (H * C) / lambda_f; // Final photon energy
        const K_e = E_i - E_f; // Kinetic energy of recoil electron
        const p_i = E_i / C; // Initial photon momentum
        const p_f = E_f / C; // Final photon momentum
        // Electron recoil angle
        const cot_phi = (1 + (E_i / (M_E * C * C))) * Math.tan(theta / 2);
        const phi_electron = Math.atan(1 / cot_phi);
        return {
            compton_wavelength_electron: lambda_c,
            wavelength_shift: delta_lambda,
            final_wavelength: lambda_f,
            initial_photon_energy: E_i,
            final_photon_energy: E_f,
            electron_kinetic_energy: K_e,
            initial_photon_momentum: p_i,
            final_photon_momentum: p_f,
            electron_recoil_angle: phi_electron,
            energy_ratio: E_f / E_i,
            wavelength_ratio: lambda_f / lambda_i,
            max_shift_at_180: 2 * lambda_c,
            is_max_shift: Math.abs(theta - Math.PI) < 0.01 ? 1 : 0,
            is_no_shift: Math.abs(theta) < 0.01 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const theta = getState(state, 'theta');
        const lambda_i = getParam(params, 'lambda_initial');
        const lambda_c = H / (M_E * C);
        const delta_lambda = lambda_c * (1 - Math.cos(theta));
        const lambda_f = lambda_i + delta_lambda;
        const E_i = (H * C) / lambda_i;
        const E_f = (H * C) / lambda_f;
        const K_e = E_i - E_f;
        return {
            kinetic: K_e,
            potential: 0,
            thermal: 0,
            total: E_f,
        };
    },
    failureModes: [
        {
            id: 'visible-light',
            name: 'Visible Light Range',
            check: (_state, params) => {
                const lambda = getParam(params, 'lambda_initial');
                return lambda > 100e-9;
            },
            severity: 'warning',
            educationalMessage: 'Compton scattering is typically observed with X-rays or gamma rays. For visible light, the wavelength shift is negligible compared to the wavelength.',
        },
    ],
    bounds: {
        theta: { min: 0, max: Math.PI },
    },
});
// ============================================================================
// BLACKBODY RADIATION
// ============================================================================
export const BlackbodyRadiation = createExperimentFromSchema({
    id: 'blackbody-radiation',
    name: 'Blackbody Radiation',
    domain: 'physics',
    description: 'Thermal electromagnetic radiation from an ideal blackbody',
    conceptTags: ['Blackbody Radiation', 'Planck\'s Law', 'Wien\'s Law', 'Stefan-Boltzmann'],
    difficulty: 6,
    stateVariables: [
        { id: 'T', name: 'Temperature', unit: 'K', initial: 5778, symbol: 'T', description: 'Surface temperature (Sun: 5778 K)' },
        { id: 'lambda', name: 'Observation Wavelength', unit: 'm', initial: 500e-9, symbol: '\\lambda' },
    ],
    parameters: [
        { id: 'T_scale', name: 'Temperature Scale', unit: 'K', min: 100, max: 50000, default: 5778, symbol: 'T_{scale}', description: 'Temperature scale factor' },
    ],
    equations: [
        {
            id: 'planck-law',
            name: "Planck's Radiation Law",
            latex: 'B_\\lambda(T) = \\frac{2hc^2}{\\lambda^5}\\frac{1}{e^{hc/\\lambda k_BT} - 1}',
            description: 'Spectral radiance per unit wavelength',
        },
        {
            id: 'wiens-law',
            name: "Wien's Displacement Law",
            latex: '\\lambda_{max}T = b \\approx 2.898 \\times 10^{-3} \\text{ m·K}',
            description: 'Wavelength of peak emission is inversely proportional to temperature',
        },
        {
            id: 'stefan-boltzmann',
            name: 'Stefan-Boltzmann Law',
            latex: 'P = \\sigma A T^4',
            description: 'Total power radiated is proportional to T⁴',
        },
        {
            id: 'wiens-constant',
            name: "Wien's Displacement Constant",
            latex: 'b = 2.898 \\times 10^{-3} \\text{ m·K}',
            description: 'Product of peak wavelength and temperature',
        },
    ],
    derivatives: () => ({
        T: 0,
        lambda: 0,
    }),
    derivedState: (state, _params) => {
        const T = getState(state, 'T');
        const lambda = getState(state, 'lambda');
        // Wien's displacement law
        const b = 2.897771955e-3; // Wien's displacement constant (m·K)
        const lambda_max = b / T;
        // Planck's law calculation
        const hc_lambda_kT = (H * C) / (lambda * K_B * T);
        const exp_term = Math.exp(hc_lambda_kT);
        const spectral_radiance = (2 * H * C * C) / (Math.pow(lambda, 5) * (exp_term - 1));
        // Stefan-Boltzmann law
        const power_per_area = STEFAN_BOLTZMANN * Math.pow(T, 4);
        // Peak spectral radiance (at lambda_max)
        const hc_lambda_max_kT = (H * C) / (lambda_max * K_B * T);
        const exp_term_max = Math.exp(hc_lambda_max_kT);
        const peak_radiance = (2 * H * C * C) / (Math.pow(lambda_max, 5) * (exp_term_max - 1));
        // Total photon flux (approximate)
        const total_photon_flux = power_per_area / (2.7 * K_B * T); // Approximate average photon energy
        // Spectral class as number (0=O, 1=B, 2=A, 3=F, 4=G, 5=K, 6=M)
        const spectral_class_num = T > 30000 ? 0 : T > 10000 ? 1 : T > 7500 ? 2 : T > 6000 ? 3 : T > 5200 ? 4 : T > 3700 ? 5 : 6;
        return {
            temperature: T,
            peak_wavelength: lambda_max,
            peak_wavelength_nm: lambda_max * 1e9,
            spectral_radiance,
            spectral_radiance_at_peak: peak_radiance,
            power_per_area,
            total_emitted_power: power_per_area,
            photon_flux_estimate: total_photon_flux,
            wiens_constant: b,
            is_in_visible: lambda_max >= 380e-9 && lambda_max <= 750e-9 ? 1 : 0,
            spectral_class: spectral_class_num,
        };
    },
    energyComputation: (state, _params) => {
        const T = getState(state, 'T');
        const power_per_area = STEFAN_BOLTZMANN * Math.pow(T, 4);
        return {
            kinetic: 0,
            potential: 0,
            thermal: power_per_area,
            total: power_per_area,
        };
    },
    failureModes: [
        {
            id: 'absolute-zero',
            name: 'At Absolute Zero',
            check: (state, _params) => {
                const T = getState(getStateValues(state), 'T');
                return T < 1;
            },
            severity: 'warning',
            educationalMessage: 'Temperature is near absolute zero. According to the third law of thermodynamics, a system cannot reach absolute zero.',
        },
    ],
    bounds: {
        T: { min: 0, max: 50000 },
        lambda: { min: 1e-9, max: 1e-3 },
    },
});
// ============================================================================
// BOHR MODEL (Hydrogen Transitions)
// ============================================================================
export const BohrModel = createExperimentFromSchema({
    id: 'bohr-model',
    name: 'Bohr Model (Hydrogen Transitions)',
    domain: 'physics',
    description: 'Electron energy levels and transitions in hydrogen atom',
    conceptTags: ['Bohr Model', 'Atomic Spectra', 'Energy Levels', 'Hydrogen Atom'],
    difficulty: 5,
    stateVariables: [
        { id: 'n_initial', name: 'Initial Energy Level', unit: '', initial: 3, symbol: 'n_i', description: 'Principal quantum number (initial state)' },
        { id: 'n_final', name: 'Final Energy Level', unit: '', initial: 2, symbol: 'n_f', description: 'Principal quantum number (final state)' },
    ],
    parameters: [
        { id: 'n_display', name: 'Max Level Display', unit: '', min: 5, max: 20, default: 10, symbol: 'n_{max}', description: 'Maximum quantum number to display' },
    ],
    equations: [
        {
            id: 'energy-levels',
            name: 'Hydrogen Energy Levels',
            latex: 'E_n = -\\frac{13.6 \\text{ eV}}{n^2}',
            description: 'Allowed electron energies in hydrogen atom',
        },
        {
            id: 'transition-energy',
            name: 'Photon Energy from Transition',
            latex: '\\Delta E = hf = E_i - E_f = 13.6\\left(\\frac{1}{n_f^2} - \\frac{1}{n_i^2}\\right) \\text{ eV}',
            description: 'Energy of emitted/absorbed photon',
        },
        {
            id: 'rydberg-formula',
            name: 'Rydberg Formula',
            latex: '\\frac{1}{\\lambda} = R_H\\left(\\frac{1}{n_f^2} - \\frac{1}{n_i^2}\\right)',
            description: 'Wavelength of emitted/absorbed light',
        },
        {
            id: 'bohr-radius',
            name: 'Bohr Radius',
            latex: 'r_n = a_0 n^2',
            description: 'Radius of electron orbit at level n (a₀ ≈ 0.529 Å)',
        },
    ],
    derivatives: () => ({
        n_initial: 0,
        n_final: 0,
    }),
    derivedState: (state, _params) => {
        const n_i = Math.round(getState(state, 'n_initial'));
        const n_f = Math.round(getState(state, 'n_final'));
        // Ground state energy of hydrogen (eV)
        const E_ground = 13.6;
        const E_i = -E_ground / (n_i * n_i);
        const E_f = -E_ground / (n_f * n_f);
        const delta_E = E_i - E_f;
        // Rydberg formula
        const lambda_inv = RYDBERG * (1 / (n_f * n_f) - 1 / (n_i * n_i));
        const lambda = 1 / lambda_inv;
        const frequency = C / lambda;
        // Bohr radius
        const a_0 = 5.29177210903e-11; // meters
        const r_i = a_0 * n_i * n_i;
        const r_f = a_0 * n_f * n_f;
        // Series identification (as numbers)
        let series = 6; // Default: Higher
        if (n_f === 1)
            series = 0; // Lyman
        else if (n_f === 2)
            series = 1; // Balmer
        else if (n_f === 3)
            series = 2; // Paschen
        else if (n_f === 4)
            series = 3; // Brackett
        else if (n_f === 5)
            series = 4; // Pfund
        const is_emission = n_i > n_f;
        const is_absorption = n_i < n_f;
        const is_in_visible = lambda >= 380e-9 && lambda <= 750e-9;
        // Spectral region: 0=UV, 1=Visible, 2=IR
        const spectral_region = lambda < 380e-9 ? 0 : lambda > 750e-9 ? 2 : 1;
        return {
            initial_energy_eV: E_i,
            final_energy_eV: E_f,
            transition_energy_eV: delta_E,
            wavelength: lambda,
            wavelength_nm: lambda * 1e9,
            frequency: frequency,
            initial_radius: r_i,
            final_radius: r_f,
            series: series,
            is_emission: is_emission ? 1 : 0,
            is_absorption: is_absorption ? 1 : 0,
            is_in_visible_range: is_in_visible ? 1 : 0,
            spectral_region: spectral_region,
            rydberg_constant: RYDBERG,
        };
    },
    energyComputation: (state, _params) => {
        const n_i = Math.round(getState(state, 'n_initial'));
        const E_ground = 13.6 * E_CHARGE; // Convert to Joules
        const E_i = -E_ground / (n_i * n_i);
        return {
            kinetic: 0,
            potential: Math.abs(E_i),
            thermal: 0,
            total: Math.abs(E_i),
        };
    },
    failureModes: [
        {
            id: 'same-level',
            name: 'No Transition',
            check: (state, _params) => {
                const stateVals = getStateValues(state);
                const n_i = Math.round(getState(stateVals, 'n_initial'));
                const n_f = Math.round(getState(stateVals, 'n_final'));
                return n_i === n_f;
            },
            severity: 'warning',
            educationalMessage: 'Initial and final states are the same. No transition occurs and no photon is emitted or absorbed.',
        },
        {
            id: 'ionization',
            name: 'Ionization State',
            check: (state, _params) => {
                const stateVals = getStateValues(state);
                const n_f = Math.round(getState(stateVals, 'n_final'));
                return n_f <= 0;
            },
            severity: 'error',
            educationalMessage: 'Final principal quantum number must be positive (n ≥ 1). n = ∞ represents ionization.',
        },
    ],
    bounds: {
        n_initial: { min: 1, max: 20 },
        n_final: { min: 1, max: 20 },
    },
});
// ============================================================================
// RADIOACTIVE DECAY (Single Isotope)
// ============================================================================
export const RadioactiveDecay = createExperimentFromSchema({
    id: 'radioactive-decay',
    name: 'Radioactive Decay (Single Isotope)',
    domain: 'physics',
    description: 'Exponential decay of a radioactive isotope',
    conceptTags: ['Radioactive Decay', 'Half-Life', 'Exponential Decay', 'Nuclear Physics'],
    difficulty: 4,
    stateVariables: [
        { id: 'N', name: 'Number of Nuclei', unit: '', initial: 10000, symbol: 'N', description: 'Number of undecayed nuclei' },
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'half_life', name: 'Half-Life', unit: 's', min: 1, max: 1e12, default: 100, symbol: 't_{1/2}', description: 'Time for half the nuclei to decay' },
        { id: 'N0', name: 'Initial Nuclei', unit: '', min: 1, max: 1e12, default: 10000, symbol: 'N_0' },
    ],
    equations: [
        {
            id: 'decay-law',
            name: 'Radioactive Decay Law',
            latex: 'N(t) = N_0 e^{-\\lambda t}',
            description: 'Number of undecayed nuclei at time t',
        },
        {
            id: 'decay-constant',
            name: 'Decay Constant',
            latex: '\\lambda = \\frac{\\ln(2)}{t_{1/2}}',
            description: 'Probability of decay per unit time',
        },
        {
            id: 'half-life',
            name: 'Half-Life',
            latex: 't_{1/2} = \\frac{\\ln(2)}{\\lambda}',
            description: 'Time for half of the nuclei to decay',
        },
        {
            id: 'activity',
            name: 'Activity',
            latex: 'A = \\lambda N = -\\frac{dN}{dt}',
            description: 'Decays per second (becquerels)',
        },
    ],
    derivatives: (state, params) => {
        const N = getState(state, 'N');
        const half_life = getParam(params, 'half_life');
        const lambda = Math.log(2) / half_life;
        const dN_dt = -lambda * N;
        return {
            N: dN_dt,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const N = getState(state, 'N');
        const t = getState(state, 't');
        const N0 = getParam(params, 'N0');
        const half_life = getParam(params, 'half_life');
        const lambda = Math.log(2) / half_life;
        const activity = lambda * N;
        const fraction_remaining = N / N0;
        const fraction_decayed = 1 - fraction_remaining;
        const mean_life = 1 / lambda;
        // Theoretical N at this time
        const N_theoretical = N0 * Math.exp(-lambda * t);
        return {
            decay_constant: lambda,
            activity: activity,
            activity_becquerels: activity,
            fraction_remaining: fraction_remaining,
            fraction_decayed: fraction_decayed,
            percent_remaining: fraction_remaining * 100,
            mean_life: mean_life,
            num_half_lives: t / half_life,
            is_half_life_reached: Math.abs(t - half_life) < 1 ? 1 : 0,
            expected_N_at_time_t: N_theoretical,
        };
    },
    failureModes: [
        {
            id: 'fully-decayed',
            name: 'Nearly Full Decay',
            check: (state, params) => {
                const N = getState(getStateValues(state), 'N');
                const N0 = getParam(params, 'N0');
                return N < N0 * 0.01;
            },
            severity: 'warning',
            educationalMessage: 'More than 99% of the original nuclei have decayed. The sample is nearly exhausted.',
        },
    ],
    bounds: {
        N: { min: 0, max: 1e12 },
        t: { min: 0, max: 10000 },
    },
});
// ============================================================================
// RADIOACTIVE DECAY CHAIN
// ============================================================================
export const RadioactiveDecayChain = createExperimentFromSchema({
    id: 'radioactive-decay-chain',
    name: 'Radioactive Decay Chain',
    domain: 'physics',
    description: 'Sequential decay through multiple isotopes (e.g., U-238 series)',
    conceptTags: ['Decay Chain', 'Secular Equilibrium', 'Bateman Equations', 'Nuclear Series'],
    difficulty: 7,
    stateVariables: [
        { id: 'N1', name: 'Parent Nuclei', unit: '', initial: 10000, symbol: 'N_1', description: 'Number of parent nuclei' },
        { id: 'N2', name: 'Daughter Nuclei', unit: '', initial: 0, symbol: 'N_2', description: 'Number of daughter nuclei' },
        { id: 'N3', name: 'Granddaughter Nuclei', unit: '', initial: 0, symbol: 'N_3', description: 'Number of granddaughter nuclei' },
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'lambda1', name: 'Decay Constant 1', unit: 's⁻¹', min: 1e-6, max: 1, default: 0.01, symbol: '\\lambda_1', description: 'Parent decay constant' },
        { id: 'lambda2', name: 'Decay Constant 2', unit: 's⁻¹', min: 1e-6, max: 1, default: 0.05, symbol: '\\lambda_2', description: 'Daughter decay constant' },
        { id: 'lambda3', name: 'Decay Constant 3', unit: 's⁻¹', min: 1e-6, max: 1, default: 0.1, symbol: '\\lambda_3', description: 'Granddaughter decay constant (stable if 0)' },
    ],
    equations: [
        {
            id: 'bateman-equations',
            name: 'Bateman Equations',
            latex: '\\frac{dN_i}{dt} = \\lambda_{i-1}N_{i-1} - \\lambda_i N_i',
            description: 'Coupled differential equations for decay chain',
        },
        {
            id: 'activity-chain',
            name: 'Activity Balance',
            latex: 'A_1 = A_2 + A_3 + \\frac{dN_3}{dt}',
            description: 'Activities are related in steady state',
        },
        {
            id: 'secular-equilibrium',
            name: 'Secular Equilibrium',
            latex: '\\lambda_1 N_1 = \\lambda_2 N_2 \\quad (\\text{when } \\lambda_2 \\gg \\lambda_1)',
            description: 'Activities equal when parent decays much slower',
        },
    ],
    derivatives: (state, params) => {
        const N1 = getState(state, 'N1');
        const N2 = getState(state, 'N2');
        const N3 = getState(state, 'N3');
        const lambda1 = getParam(params, 'lambda1');
        const lambda2 = getParam(params, 'lambda2');
        const lambda3 = getParam(params, 'lambda3');
        const dN1_dt = -lambda1 * N1;
        const dN2_dt = lambda1 * N1 - lambda2 * N2;
        const dN3_dt = lambda2 * N2 - lambda3 * N3;
        return {
            N1: dN1_dt,
            N2: dN2_dt,
            N3: dN3_dt,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const N1 = getState(state, 'N1');
        const N2 = getState(state, 'N2');
        const N3 = getState(state, 'N3');
        const lambda1 = getParam(params, 'lambda1');
        const lambda2 = getParam(params, 'lambda2');
        const lambda3 = getParam(params, 'lambda3');
        const A1 = lambda1 * N1;
        const A2 = lambda2 * N2;
        const A3 = lambda3 * N3;
        const half_life1 = Math.log(2) / lambda1;
        const half_life2 = Math.log(2) / lambda2;
        const half_life3 = lambda3 > 0 ? Math.log(2) / lambda3 : 0;
        // Check for secular equilibrium (A1 ≈ A2 ≈ A3)
        const tolerance = 0.05;
        const equilibrium_12 = Math.abs(A1 - A2) / Math.max(A1, A2) < tolerance && A2 > tolerance;
        const equilibrium_23 = Math.abs(A2 - A3) / Math.max(A2, A3) < tolerance && A3 > tolerance;
        const secular_equilibrium = equilibrium_12 && equilibrium_23;
        return {
            activity_parent: A1,
            activity_daughter: A2,
            activity_granddaughter: A3,
            half_life_parent: half_life1,
            half_life_daughter: half_life2,
            half_life_granddaughter: half_life3,
            secular_equilibrium: secular_equilibrium ? 1 : 0,
            activity_ratio_2_1: A1 > 0 ? A2 / A1 : 0,
            activity_ratio_3_2: A2 > 0 ? A3 / A2 : 0,
            total_activity: A1 + A2 + A3,
            is_parent_dominant: lambda1 < lambda2 ? 1 : 0,
            is_transient_equilibrium: lambda1 < lambda2 && Math.abs(A1 / A2 - lambda2 / (lambda2 - lambda1)) < 0.1 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const N1 = getState(state, 'N1');
        const N2 = getState(state, 'N2');
        const N3 = getState(state, 'N3');
        const lambda1 = getParam(params, 'lambda1');
        const lambda2 = getParam(params, 'lambda2');
        const lambda3 = getParam(params, 'lambda3');
        // Activity represents power (decays per second)
        const power = (lambda1 * N1 + lambda2 * N2 + lambda3 * N3) * 1e6; // Scaling factor
        return {
            kinetic: 0,
            potential: 0,
            thermal: power,
            total: power,
        };
    },
    failureModes: [
        {
            id: 'no-parents',
            name: 'No Parent Nuclei',
            check: (state, _params) => {
                const N1 = getState(getStateValues(state), 'N1');
                return N1 < 1;
            },
            severity: 'warning',
            educationalMessage: 'All parent nuclei have decayed. The chain will continue as daughter nuclei decay.',
        },
    ],
    bounds: {
        N1: { min: 0, max: 1e12 },
        N2: { min: 0, max: 1e12 },
        N3: { min: 0, max: 1e12 },
        t: { min: 0, max: 10000 },
    },
});
//# sourceMappingURL=modern-physics.js.map